import { Grid } from '@material-ui/core';
import DataTable from "components/DataTable/DataTable";
import LoadingSpinner from 'components/LoadingSpinner';
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { userHasOneClient } from "features/user/userSlice";
import useSortOptions from "hooks/useSortOptions";
import _, { orderBy } from "lodash";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import FilterService, { fieldTypes } from "services/filterService";
import FormatService from 'services/formatService';
import { arrayToObject } from "utils/object-util";
import { timeDwellingObj, timeDwellingGroups } from "./DwellConsts";

const AssetDwellTable = ({
    assetData,
    title,
}) => {
    let hasOneClaim = useSelector(userHasOneClient);

    let { data: lots, error: lotsError, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: clients, error: clientError, isLoading: isLoadingClients } = useGetClientsQuery();

    const clientAccounts = _.flatten(clients?.map(c => c.clientAccounts));

    const [sortOptions, sortData] = useSortOptions();
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({});

    const filterConfig = [
        ...(hasOneClaim ? [] : [
            {
                type: fieldTypes.TEXT, size: 2, title: 'Client', key: 'clientId', values: clientAccounts?.filter(c => c.isDefault)?.map(acc => ({
                    id: acc?.clientId,
                    name: acc?.name
                }))
            }]),
        { type: fieldTypes.LOT, size: 2, title: 'Lot', key: 'lotId', values: lots, multiple: true },
        { type: fieldTypes.NUMBERRANGE, size: 2, title: 'Days On Lot', key: 'daysOnLot', values: [0, 30, 60] },
    ];

    let subClients = clientAccounts?.filter(a =>
        !a.isDefault &&
        (filters?.clientId && filters?.clientId?.value?.length > 0 ? filters?.clientId.value?.includes(a.clientId) : true)
    )


    if (subClients?.length > 0) {
        filterConfig.splice(1, 0, {
            type: fieldTypes.TEXT,
            size: 2,
            title: 'Sub Client',
            key: 'clientAccountId',
            values: subClients,
            multiple: false
        });
    }

    const lotsLookup = useMemo(() => arrayToObject(lots), [lots]);
    const clientLookup = useMemo(() => arrayToObject(clients), [clients]);
    const clientAccountsLookup = useMemo(() => arrayToObject(clientAccounts), [clientAccounts]);
    const CALookupWithoutDefault = useMemo(() => arrayToObject(clientAccounts?.filter(c => c.isDefault == false)), [clientAccounts]);


    const mappedData = useMemo(() => assetData?.map(asset => {
        var client = clientLookup?.[asset.clientId];
        var clientAccount = clientAccountsLookup[asset.clientAccountId];
        return {
            ...asset,
            client: client?.name,
            subClient: clientAccount ? asset.clientAccountId : null,
            clientName: client?.name + (client?.name != clientAccount?.name ? ": " + clientAccount?.name : ""),
            lot: lotsLookup?.[asset.lotId]?.name,
            dateInFormated: FormatService.formatDateNoConversion(asset.dateIn),
            dateOutFormated: FormatService.formatDateNoConversion(asset.dateOut),
        }
    }), [assetData, lotsLookup, clientLookup, clientAccountsLookup]);

    const mapExportData = asset => {
        let allDataMap = {
            ...asset,
            Client: clientLookup?.[asset.clientId]?.name,
            VIN: asset.descriptor,
            ["Asset Id"]: asset.assetId,
            ["Sub Client"]: CALookupWithoutDefault[asset.clientAccountId]?.name ?? "",
            "Date In": FormatService.formatDateNoConversion(asset.dateIn),
            "Date Out": FormatService.formatDateNoConversion(asset.dateOut),
            Lot: lotsLookup?.[asset.lotId]?.name,
            "Days On Lot": asset.daysOnLot
        }
        return allDataMap;
    };

    const getDwellColor = (days) => {
        if (days == null || isNaN(days)) return undefined;
        if (days < 30) return timeDwellingObj[timeDwellingGroups.LESS_THAN_30_DAYS].color;
        if (days < 60) return timeDwellingObj[timeDwellingGroups.MORE_THAN_30_DAYS].color;
        return timeDwellingObj[timeDwellingGroups.MORE_THAN_60_DAYS].color;
    };

    let assetColumns = [
        ...(hasOneClaim ? [] : [{ name: "Client", key: "client" }]),
        { name: "VIN", key: "descriptor", width: 200, component: (row) => <Link target='_blank' to={`/assets/detail/${row?.id}`} >{row?.descriptor}</Link> },
        { name: "Asset Id", key: "assetId", width: 200, component: (row) => <Link target='_blank' to={`/assets/detail/${row?.id}`}>{row?.assetId}</Link> },
        { name: "Sub Client", key: "subClient", values: clientAccounts?.filter(a => a.isDefault == false), width: 200 },
        { name: "Date In", key: "dateInFormated" },
        { name: "Date Out", key: "dateOutFormated" },
        { name: "Lot", key: "lot" },
        {
            name: "Days On Lot", key: "daysOnLot", component: (row) => {
                const days = row?.daysOnLot;
                const color = getDwellColor(days);
                return (
                    <div style={{ color }}>
                        {days ?? "-"}
                    </div>
                );
            }
        },
    ];

    let filteredData = FilterService.filter(filters, search, mappedData, filterConfig);

    const onDropFilter = (filterValue, prop) => {
        setFilters({ ...filters, [prop]: { value: filterValue } });
    };

    const onResetFilters = () => {
        setFilters({});
    };

    const isLoading = isLoadingLots || isLoadingClients;;

    return (
        <div >
            {isLoading && <LoadingSpinner loading={isLoading} />}
            <DataTable
                title={<div>
                    <Grid container alignItems='center' spacing={1}>
                        {title} ({filteredData?.length})
                    </Grid>
                </div>}
                onSearch={setSearch}
                dropDownsConfig={filterConfig}
                filters={filters}
                onDropFilter={onDropFilter}
                onResetFilters={onResetFilters}
                withFilterPanel
                columns={assetColumns}
                rows={orderBy(
                    filteredData,
                    sortOptions.columnToSort,
                    sortOptions.sortDirection
                )}
                rowIdentifier='id'
                onSort={sortData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}
                noItemsMessage='There are no vehicles matching this criteria'
                mapExportData={mapExportData}
            />
        </div>

    )
}

export default AssetDwellTable;
